import React, { useMemo, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import Select from "components/Select";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import {
  useGetTenants,
  useGetAppIntegrations,
  useGetAppIntegrationApiCatalog,
  useCreateAppIntegration,
  useUpdateAppIntegration,
  useRotateAppIntegrationSecret,
  useGetAppIntegrationUsers,
  useIssueAppIntegrationUserToken,
  useGetSAUsers,
} from "api/super-admin/service";
import { getActiveColorStyle } from "utils/colorUtils";
import { getTheme2SearchFilterSingleSelectStyles } from "utils/theme2MultiSelect";
import { getStickyStockTableStickyColumnCss } from "utils/reactTableTheme";

const PAGE_STYLE = `
.app-integration-page .nav-tabs {
  border-bottom: none !important;
  background: transparent !important;
}
.app-integration-page .nav-tabs .nav-item .nav-link,
.app-integration-page .nav-tabs .nav-link {
  background: transparent !important;
  background-image: none !important;
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  padding: 0.5rem 0.9rem;
  opacity: 1 !important;
}
.app-integration-page .nav-tabs .nav-link.active,
.app-integration-page .nav-tabs .nav-link:hover,
.app-integration-page .nav-tabs .nav-link:focus,
.app-integration-page .nav-tabs .nav-link.active:hover,
.app-integration-page .nav-tabs .nav-link.active:focus {
  background: transparent !important;
  background-image: none !important;
  border: none !important;
  box-shadow: none !important;
}
/* Dark theme: inactive tabs readable white (same as account/user detail tabs) */
body:not(.white-content) .app-integration-page .nav-tabs .nav-link:not(.active),
body:not(.white-content) .app-integration-page .nav-tabs .nav-link:not(.active):hover {
  color: #ffffff !important;
}
/* Light theme: inactive tabs use standard body text, not muted grey */
body.white-content .app-integration-page .nav-tabs .nav-link:not(.active),
body.white-content .app-integration-page .nav-tabs .nav-link:not(.active):hover {
  color: #525f7f !important;
}
.app-integration-page .mi-code-block {
  margin: 0;
  padding: 0.85rem 1rem;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 12px;
  line-height: 1.45;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.28);
  color: inherit;
}
body.white-content .app-integration-page .mi-code-block {
  border-color: rgba(0, 0, 0, 0.12);
  background: rgba(0, 0, 0, 0.04);
  color: #222a42;
}
.app-integration-page .mi-inline-code {
  color: inherit;
  opacity: 0.95;
  word-break: break-all;
}
.app-integration-page .mi-section-title {
  margin-bottom: 0.75rem;
}
.app-integration-page .mi-muted {
  opacity: 0.75;
}
.app-integration-page .mi-endpoint + .mi-endpoint {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
body.white-content .app-integration-page .mi-endpoint + .mi-endpoint {
  border-top-color: rgba(0, 0, 0, 0.08);
}
/* Keep main-panel scrollable on this page (not a full-height list table layout) */
.sidebar.sidebar-shell-theme-2 ~ .main-panel:has(> .content.app-integration-page) {
  overflow-x: hidden !important;
  overflow-y: auto !important;
  height: auto !important;
  max-height: none !important;
}
/* API Docs: long content must scroll with the page */
.app-integration-page.app-integration-page--docs-open,
.app-integration-page .tab-content,
.app-integration-page .tab-pane.active,
.app-integration-page .card,
.app-integration-page .card-body {
  overflow: visible !important;
  max-height: none !important;
  height: auto !important;
}
.app-integration-page .mi-api-docs {
  overflow: visible;
  padding-bottom: 2rem;
}
.app-integration-page .mi-api-docs .mi-code-block {
  max-width: 100%;
  overflow-x: auto;
}
.mi-credentials-modal .modal-content,
.mi-credentials-modal-content {
  background: #1e1e2f !important;
  color: #ffffff !important;
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.mi-credentials-modal .modal-header,
.mi-credentials-modal .modal-footer {
  border-color: rgba(255, 255, 255, 0.1) !important;
}
.mi-credentials-modal .modal-title,
.mi-credentials-modal .modal-header .close span,
.mi-credentials-modal label {
  color: #ffffff !important;
}
.mi-credentials-modal .mi-secret-input.form-control,
.mi-credentials-modal .mi-secret-input.form-control:focus,
.mi-credentials-modal .mi-secret-input.form-control[readonly],
.mi-credentials-modal .form-control,
.mi-credentials-modal .form-control:focus,
.mi-credentials-modal .form-control[readonly] {
  background-color: rgba(0, 0, 0, 0.45) !important;
  color: #ffffff !important;
  border: 1px solid rgba(255, 255, 255, 0.22) !important;
  opacity: 1 !important;
  -webkit-text-fill-color: #ffffff !important;
  caret-color: #ffffff;
}
body.white-content .mi-credentials-modal .modal-content,
body.white-content .mi-credentials-modal-content {
  background: #ffffff !important;
  color: #222a42 !important;
}
body.white-content .mi-credentials-modal .modal-header,
body.white-content .mi-credentials-modal .modal-footer {
  border-color: rgba(0, 0, 0, 0.08) !important;
}
body.white-content .mi-credentials-modal .modal-title,
body.white-content .mi-credentials-modal .modal-header .close span,
body.white-content .mi-credentials-modal label {
  color: #222a42 !important;
}
body.white-content .mi-credentials-modal .mi-secret-input.form-control,
body.white-content .mi-credentials-modal .mi-secret-input.form-control:focus,
body.white-content .mi-credentials-modal .mi-secret-input.form-control[readonly],
body.white-content .mi-credentials-modal .form-control,
body.white-content .mi-credentials-modal .form-control:focus,
body.white-content .mi-credentials-modal .form-control[readonly] {
  background-color: #f4f5f7 !important;
  color: #222a42 !important;
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
  -webkit-text-fill-color: #222a42 !important;
  caret-color: #222a42;
}
`;

const copyText = async (value) => {
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
  } catch (_) {
    // ignore
  }
};

const UnderlineTab = ({ active, activeColorStyle, onClick, children }) => (
  <NavItem>
    <NavLink
      href="#"
      className={active ? "active font-weight-bold" : undefined}
      style={{
        background: "none",
        border: "none",
        borderRadius: 0,
        position: "relative",
        cursor: "pointer",
        color: active ? activeColorStyle.borderColor : undefined,
        fontWeight: active ? 700 : 500,
        fontSize: 14,
        boxShadow: "none",
      }}
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      {children}
      {active && (
        <div
          style={{
            height: 3,
            background: activeColorStyle.borderColor,
            position: "absolute",
            left: 0,
            right: 0,
            bottom: -8,
          }}
        />
      )}
    </NavLink>
  </NavItem>
);

const SecretRevealModal = ({ isOpen, onClose, title, secretPayload, t }) => {
  const integration = secretPayload?.integration || secretPayload;
  const clientSecret = integration?.client_secret;
  const accessToken = secretPayload?.access_token;

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="lg"
      className="mi-credentials-modal modal-black"
      contentClassName="mi-credentials-modal-content"
    >
      <style>{PAGE_STYLE}</style>
      <ModalHeader toggle={onClose}>{title}</ModalHeader>
      <ModalBody>
        <p className="text-warning mb-3">
          {integration?.secret_notice || t("appIntegration.secretOnceWarning")}
        </p>
        {integration?.client_id && (
          <FormGroup>
            <Label>client_id</Label>
            <div className="d-flex" style={{ gap: 8 }}>
              <Input className="mi-secret-input" readOnly value={integration.client_id} />
              <Button color="secondary" outline onClick={() => copyText(integration.client_id)}>
                {t("appIntegration.copy")}
              </Button>
            </div>
          </FormGroup>
        )}
        {clientSecret && (
          <FormGroup>
            <Label>client_secret</Label>
            <div className="d-flex" style={{ gap: 8 }}>
              <Input className="mi-secret-input" readOnly value={clientSecret} />
              <Button color="secondary" outline onClick={() => copyText(clientSecret)}>
                {t("appIntegration.copy")}
              </Button>
            </div>
          </FormGroup>
        )}
        {accessToken && (
          <FormGroup>
            <Label>access_token</Label>
            <div className="d-flex" style={{ gap: 8 }}>
              <Input
                className="mi-secret-input"
                type="textarea"
                rows={4}
                readOnly
                value={accessToken}
              />
              <Button color="secondary" outline onClick={() => copyText(accessToken)}>
                {t("appIntegration.copy")}
              </Button>
            </div>
            <small className="mi-muted">
              {t("appIntegration.tokenExpiresIn")}: {secretPayload?.expires_in || "24h"}
            </small>
          </FormGroup>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={onClose}>
          {t("appIntegration.done")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const ApiDocsPanel = ({ catalog, apiBase }) => {
  const { t } = useTranslation();
  if (!catalog) return null;

  const buildCurl = (item) => {
    const rawPath = String(item.path || "");
    const [pathOnly, embeddedQuery] = rawPath.split("?");
    const fullPath = `${apiBase || ""}${pathOnly}`;

    const queryFromItem = item.query || {};
    const queryEntries = [
      ...Object.entries(
        embeddedQuery
          ? Object.fromEntries(new URLSearchParams(embeddedQuery))
          : {}
      ),
      ...Object.entries(queryFromItem),
    ];
    // de-dupe by key, prefer item.query
    const queryMap = {};
    queryEntries.forEach(([k, v]) => {
      queryMap[k] = v;
    });
    const qs =
      Object.keys(queryMap).length > 0
        ? `?${Object.entries(queryMap)
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
            .join("&")}`
        : "";

    if (item.method === "GET") {
      return `curl -X GET "${fullPath}${qs}" \\\n  -H "Authorization: Bearer <access_token>"`;
    }

    const bodyJson = item.body ? JSON.stringify(item.body) : "";
    return [
      `curl -X ${item.method} "${fullPath}${qs}" \\`,
      `  -H "Authorization: Bearer <access_token>" \\`,
      `  -H "Content-Type: application/json"${bodyJson ? " \\" : ""}`,
      bodyJson ? `  -d '${bodyJson}'` : null,
    ]
      .filter(Boolean)
      .join("\n");
  };

  return (
    <div className="mi-api-docs">
      <div className="mb-4">
        <h4 className="mi-section-title">{catalog.overview?.title}</h4>
        <p className="mb-3">{catalog.overview?.summary}</p>
        <ol className="mb-0 pl-3">
          {(catalog.overview?.steps || []).map((step, idx) => (
            <li key={idx} className="mb-1">
              {step}
            </li>
          ))}
        </ol>
        <p className="mt-3 mb-0">
          <strong>{t("appIntegration.authHeader")}:</strong>{" "}
          <code className="mi-inline-code">{catalog.authHeader}</code>
        </p>
      </div>

      {(catalog.catalog || []).map((group) => (
        <div key={group.group} className="mb-4">
          <h5 className="mi-section-title">{group.group}</h5>
          {(group.items || []).map((item) => {
            const curl = buildCurl(item);
            return (
              <div key={`${item.method}-${item.path}`} className="mi-endpoint">
                <div className="d-flex align-items-center flex-wrap mb-2" style={{ gap: 8 }}>
                  <Badge color="info">{item.method}</Badge>
                  <code className="mi-inline-code">{item.path}</code>
                </div>
                <p className="mb-2">{item.description}</p>
                {item.notes && <p className="mi-muted small mb-2">{item.notes}</p>}

                {item.query && (
                  <div className="mb-2">
                    <strong className="d-block mb-1">{t("appIntegration.queryParams")}</strong>
                    <pre className="mi-code-block mb-0">{JSON.stringify(item.query, null, 2)}</pre>
                  </div>
                )}

                {Array.isArray(item.types) && item.types.length > 0 && (
                  <div className="mb-2">
                    <strong className="d-block mb-1">{t("appIntegration.typeValues")}</strong>
                    <ul className="mb-0 pl-3">
                      {item.types.map((typeItem) => (
                        <li key={typeItem.value} className="mb-1">
                          <code className="mi-inline-code">{typeItem.value}</code>
                          {typeItem.description ? (
                            <span className="mi-muted"> — {typeItem.description}</span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {Array.isArray(item.required) && item.required.length > 0 && (
                  <div className="mb-2">
                    <strong className="d-block mb-1">{t("appIntegration.requiredFields")}</strong>
                    <ul className="mb-0 pl-3">
                      {item.required.map((field) => (
                        <li key={field}>
                          <code className="mi-inline-code">{field}</code>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {Array.isArray(item.optional) && item.optional.length > 0 && (
                  <div className="mb-2">
                    <strong className="d-block mb-1">{t("appIntegration.optionalFields")}</strong>
                    <p className="small mi-muted mb-0" style={{ wordBreak: "break-word" }}>
                      {item.optional.join(", ")}
                    </p>
                  </div>
                )}

                {item.body && (
                  <div className="mb-2">
                    <strong className="d-block mb-1">{t("appIntegration.requestBody")}</strong>
                    <pre className="mi-code-block mb-0">{JSON.stringify(item.body, null, 2)}</pre>
                    <Button
                      size="sm"
                      color="secondary"
                      outline
                      className="mt-2"
                      onClick={() => copyText(JSON.stringify(item.body, null, 2))}
                    >
                      {t("appIntegration.copyBody")}
                    </Button>
                  </div>
                )}

                {Array.isArray(item.example_bodies) &&
                  item.example_bodies.map((example) => (
                    <div key={example.title} className="mb-2">
                      <strong className="d-block mb-1">{example.title}</strong>
                      <pre className="mi-code-block mb-0">
                        {JSON.stringify(example.body, null, 2)}
                      </pre>
                    </div>
                  ))}

                <div className="mb-2">
                  <strong className="d-block mb-1">cURL</strong>
                  <pre className="mi-code-block mb-2">{curl}</pre>
                  <Button size="sm" color="secondary" outline onClick={() => copyText(curl)}>
                    {t("appIntegration.copyCurl")}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const LinkedUsersPanel = ({
  integrationId,
  tenantCode,
  isTheme2,
  IntegrationsTable,
  activeColor,
  selectStyles,
}) => {
  const { t } = useTranslation();
  const [userOption, setUserOption] = useState(null);
  const [secretPayload, setSecretPayload] = useState(null);

  const { data: linkedUsers, isLoading } = useGetAppIntegrationUsers(integrationId, !!integrationId);
  const { data: usersResponse } = useGetSAUsers(
    1,
    200,
    tenantCode ? String(tenantCode) : "",
    "",
    !!tenantCode
  );
  const issueToken = useIssueAppIntegrationUserToken();

  const userOptions = useMemo(() => {
    const list = usersResponse?.results || usersResponse || [];
    return (Array.isArray(list) ? list : []).map((user) => ({
      value: user.id,
      label: `${user.user_detail?.full_name || user.username} (#${user.id})`,
    }));
  }, [usersResponse]);

  const columns = useMemo(
    () => [
      {
        Header: t("appIntegration.dealerUser"),
        accessor: "user",
        Cell: ({ value }) =>
          value ? `${value.full_name || value.username || "-"} (#${value.id})` : "-",
      },
      {
        Header: t("appIntegration.status"),
        accessor: "status",
        Cell: ({ value }) => (
          <Badge color={value === "active" ? "success" : "secondary"}>{value}</Badge>
        ),
      },
      {
        Header: t("appIntegration.lastIssued"),
        accessor: "last_issued_at",
        Cell: ({ value }) => (value ? new Date(value).toLocaleString() : "-"),
      },
    ],
    [t]
  );

  const onIssue = async () => {
    if (!userOption?.value) return;
    const result = await issueToken.mutateAsync({
      id: integrationId,
      data: {
        user_id: userOption.value,
      },
    });
    setSecretPayload(result);
  };

  return (
    <div>
      <div className="mb-4">
        <h5 className="mi-section-title">{t("appIntegration.issueUserToken")}</h5>
        <p className="mi-muted small mb-3">{t("appIntegration.issueUserTokenHelp")}</p>
        <Row>
          <Col md="8">
            <FormGroup>
              <Label>{t("appIntegration.dealerUser")}</Label>
              <Select
                options={userOptions}
                value={userOption}
                onChange={setUserOption}
                placeholder={t("appIntegration.selectUser")}
                styles={selectStyles}
              />
            </FormGroup>
          </Col>
          <Col md="4" className="d-flex align-items-end">
            <FormGroup className="w-100">
              <Button
                color="primary"
                className="w-100"
                disabled={!userOption || issueToken.isPending}
                onClick={onIssue}
              >
                {issueToken.isPending ? <Spinner size="sm" /> : t("appIntegration.issueToken")}
              </Button>
            </FormGroup>
          </Col>
        </Row>
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <div
          className="inventory-table-wrap"
          style={{ fontSize: isTheme2 ? "13px" : "12px", minWidth: 0, maxWidth: "100%" }}
        >
          <IntegrationsTable
            activeColor={activeColor}
            data={Array.isArray(linkedUsers) ? linkedUsers : []}
            columns={columns}
            showPaginationBottom={false}
            disableViewportClamp
            className={classNames("-striped -highlight", { "sticky-stock-table": isTheme2 })}
          />
        </div>
      )}

      <SecretRevealModal
        isOpen={!!secretPayload}
        onClose={() => setSecretPayload(null)}
        title={t("appIntegration.userTokenReady")}
        secretPayload={secretPayload}
        t={t}
      />
    </div>
  );
};

const AppIntegration = ({ activeColor = "blue", shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const isTheme2 = shellThemeVariant === "2";
  const IntegrationsTable = isTheme2 ? ReactTable2 : ReactTable;
  const selectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );

  const [activeTab, setActiveTab] = useState("1");
  const [createName, setCreateName] = useState("");
  const [createTenant, setCreateTenant] = useState(null);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [secretPayload, setSecretPayload] = useState(null);

  const { data: tenantsData, isLoading: isLoadingTenants } = useGetTenants(1, 1000);
  const { data: integrations, isLoading, refetch } = useGetAppIntegrations();
  const { data: catalog } = useGetAppIntegrationApiCatalog(activeTab === "2");
  const createMutation = useCreateAppIntegration();
  const updateMutation = useUpdateAppIntegration();
  const rotateMutation = useRotateAppIntegrationSecret();

  const tenantOptions = useMemo(
    () =>
      (tenantsData?.data || tenantsData?.results || []).map((row) => ({
        value: row.id,
        label: `${row.name} (${row.code})`,
      })),
    [tenantsData]
  );

  const apiBase = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

  const columns = useMemo(
    () => [
      {
        Header: t("appIntegration.tenant"),
        accessor: "tenant",
        Cell: ({ value }) => value?.name || "-",
      },
      { Header: t("appIntegration.name"), accessor: "name" },
      {
        Header: "client_id",
        accessor: "client_id",
        Cell: ({ value }) => <code className="mi-inline-code small">{value}</code>,
      },
      {
        Header: t("appIntegration.status"),
        accessor: "status",
        Cell: ({ value }) => (
          <Badge color={value === "active" ? "success" : "secondary"}>{value}</Badge>
        ),
      },
      {
        Header: t("appIntegration.actions"),
        id: "actions",
        Cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="d-flex flex-wrap" style={{ gap: 6 }}>
              <Button
                size="sm"
                color="info"
                outline
                onClick={() => {
                  setSelectedIntegration(item);
                  setActiveTab("3");
                }}
              >
                {t("appIntegration.users")}
              </Button>
              <Button
                size="sm"
                color="warning"
                outline
                disabled={rotateMutation.isPending}
                onClick={async () => {
                  const result = await rotateMutation.mutateAsync(item.id);
                  setSecretPayload(result);
                  refetch();
                }}
              >
                {t("appIntegration.rotateSecret")}
              </Button>
              <Button
                size="sm"
                color={item.status === "active" ? "secondary" : "success"}
                outline
                disabled={updateMutation.isPending}
                onClick={async () => {
                  await updateMutation.mutateAsync({
                    id: item.id,
                    data: { status: item.status === "active" ? "revoked" : "active" },
                  });
                  refetch();
                }}
              >
                {item.status === "active"
                  ? t("appIntegration.revoke")
                  : t("appIntegration.activate")}
              </Button>
            </div>
          );
        },
      },
    ],
    [t, rotateMutation, updateMutation, refetch]
  );

  const onCreate = async () => {
    if (!createTenant?.value) return;
    const result = await createMutation.mutateAsync({
      tenant_id: createTenant.value,
      name: createName || undefined,
    });
    setSecretPayload(result);
    setCreateName("");
    setCreateTenant(null);
    refetch();
  };

  return (
    <div
      className={classNames("content app-integration-page search-app-integration", {
        "search-app-integration--theme2": isTheme2,
        "app-integration-page--docs-open": activeTab === "2",
      })}
    >
      <style>{PAGE_STYLE}</style>
      {isTheme2 ? (
        <style>{getStickyStockTableStickyColumnCss(".app-integration-page")}</style>
      ) : null}
      <Row>
        <Col xs="12">
          <Card>
            <CardHeader className="search-app-integration-card-header">
              <h4 className="mb-1">{t("appIntegration.title")}</h4>
              <p className="mi-muted mb-0">{t("appIntegration.subtitle")}</p>
            </CardHeader>
            <CardBody>
              <div className="mb-4 pb-2 app-integration-detail-tabs user-detail-tabs">
                <Nav tabs className="border-0 mb-0" style={{ borderBottom: "none" }}>
                  <UnderlineTab
                    active={activeTab === "1"}
                    activeColorStyle={activeColorStyle}
                    onClick={() => setActiveTab("1")}
                  >
                    {t("appIntegration.tabIntegrations")}
                  </UnderlineTab>
                  <UnderlineTab
                    active={activeTab === "2"}
                    activeColorStyle={activeColorStyle}
                    onClick={() => setActiveTab("2")}
                  >
                    {t("appIntegration.tabApiDocs")}
                  </UnderlineTab>
                  <UnderlineTab
                    active={activeTab === "3"}
                    activeColorStyle={activeColorStyle}
                    onClick={() => setActiveTab("3")}
                  >
                    {t("appIntegration.tabUserTokens")}
                  </UnderlineTab>
                </Nav>
              </div>

              <TabContent activeTab={activeTab}>
                <TabPane tabId="1">
                  <div className="mb-4">
                    <h5 className="mi-section-title">{t("appIntegration.createTitle")}</h5>
                    <Row>
                      <Col md="5">
                        <FormGroup>
                          <Label>{t("appIntegration.tenant")}</Label>
                          <Select
                            options={tenantOptions}
                            value={createTenant}
                            onChange={setCreateTenant}
                            isLoading={isLoadingTenants}
                            placeholder={t("appIntegration.selectTenant")}
                            styles={selectStyles}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label>{t("appIntegration.name")}</Label>
                          <Input
                            value={createName}
                            onChange={(e) => setCreateName(e.target.value)}
                            placeholder="Partner App"
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3" className="d-flex align-items-end">
                        <FormGroup className="w-100">
                          <Button
                            color="primary"
                            className="w-100"
                            style={
                              createTenant
                                ? { ...activeColorStyle, color: "#fff", border: "none" }
                                : undefined
                            }
                            disabled={!createTenant || createMutation.isPending}
                            onClick={onCreate}
                          >
                            {createMutation.isPending ? (
                              <Spinner size="sm" />
                            ) : (
                              t("appIntegration.create")
                            )}
                          </Button>
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>

                  {isLoading ? (
                    <Spinner />
                  ) : (
                    <div
                      className="inventory-table-wrap"
                      style={{
                        fontSize: isTheme2 ? "13px" : "12px",
                        minWidth: 0,
                        maxWidth: "100%",
                      }}
                    >
                      <IntegrationsTable
                        activeColor={activeColor}
                        data={Array.isArray(integrations) ? integrations : []}
                        columns={columns}
                        showPaginationBottom={false}
                        disableViewportClamp
                        className={classNames("-striped -highlight", {
                          "sticky-stock-table": isTheme2,
                        })}
                      />
                    </div>
                  )}
                </TabPane>

                <TabPane tabId="2">
                  <ApiDocsPanel catalog={catalog} apiBase={apiBase} />
                </TabPane>

                <TabPane tabId="3">
                  {!selectedIntegration ? (
                    <p className="mi-muted">{t("appIntegration.selectIntegrationFirst")}</p>
                  ) : (
                    <>
                      <p className="mb-3">
                        <strong>{selectedIntegration.name}</strong>
                        <span className="mi-muted"> — {selectedIntegration.tenant?.name}</span>
                        <code className="mi-inline-code ms-2">
                          {selectedIntegration.client_id}
                        </code>
                      </p>
                      <LinkedUsersPanel
                        integrationId={selectedIntegration.id}
                        tenantCode={selectedIntegration.tenant?.code}
                        isTheme2={isTheme2}
                        IntegrationsTable={IntegrationsTable}
                        activeColor={activeColor}
                        selectStyles={selectStyles}
                      />
                    </>
                  )}
                </TabPane>
              </TabContent>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <SecretRevealModal
        isOpen={!!secretPayload}
        onClose={() => setSecretPayload(null)}
        title={t("appIntegration.credentialsReady")}
        secretPayload={secretPayload}
        t={t}
      />
    </div>
  );
};

export default AppIntegration;
